'use client';
import { useState, useRef, useMemo } from 'react';
import { useToast } from '@practium/ui';
import {
	useGetHierarchies,
	useCreateManagementPlan,
	useUpdateManagementPlan,
} from '@practium/features/api';
import type { PlanFormData, PaymentMethod, PlanInterval } from '../types';
import { PlanSchema, PLAN_FIELD_LABELS } from '../schemas/system.schema';
import { DEFAULT_PLAN_FORM } from '../constants/system.constants';

export interface UsePlanFormOptions {
	id?: string;
	initialData?: PlanFormData;
	onSubmit: () => void;
}

export const usePlanForm = ({ id, initialData, onSubmit }: UsePlanFormOptions) => {
	const toast = useToast();
	const { mutateAsync: createPlan } = useCreateManagementPlan();
	const { mutateAsync: updatePlan } = useUpdateManagementPlan();
	const nextId = useRef(0);
	const newId = () => ++nextId.current;
	const initialForm = initialData ? { ...DEFAULT_PLAN_FORM, ...initialData } : { ...DEFAULT_PLAN_FORM };
	const [form, setForm] = useState<PlanFormData>(initialForm);
	const initialFormRef = useRef(initialForm);
	const initialAdvantageCount = form.advantages.length || 1;
	const [advantageKeys, setAdvantageKeys] = useState<number[]>(() =>
		Array.from({ length: initialAdvantageCount }, newId)
	);
	const [isLoading, setIsLoading] = useState(false);
	const [errors, setErrors] = useState<Record<string, string>>({});

	const { data: hierarchyDTOs } = useGetHierarchies();
	const hierarchyOptions = useMemo(
		() => hierarchyDTOs.map(h => ({ value: h._id, label: h.name })),
		[hierarchyDTOs]
	);

	const set = <K extends keyof Omit<PlanFormData, 'advantages' | 'paymentMethods'>>(
		key: K,
		value: PlanFormData[K]
	) => setForm(f => ({ ...f, [key]: value }));

	const setPaymentMethods = (methods: PaymentMethod[]) =>
		setForm(f => ({ ...f, paymentMethods: methods }));

	const setAdvantage = (index: number, value: string) => {
		setForm(f => {
			const advantages = f.advantages.map((a, i) => (i === index ? value : a));
			return { ...f, advantages };
		});
	};

	const addAdvantage = () => {
		setAdvantageKeys(k => [...k, newId()]);
		setForm(f => ({ ...f, advantages: [...f.advantages, ''] }));
	};

	const removeAdvantage = (index: number) => {
		setAdvantageKeys(k => k.filter((_, i) => i !== index));
		setForm(f => ({ ...f, advantages: f.advantages.filter((_, i) => i !== index) }));
	};

	const isFree = form.paymentMethods.includes('free');
	const hasCard = form.paymentMethods.includes('credit_card');

	const isDirty = () => JSON.stringify(form) !== JSON.stringify(initialFormRef.current);

	const handleSubmit = async () => {
		const result = PlanSchema.safeParse(form);

		if (!result.success) {
			const fieldErrors: Record<string, string> = {};
			result.error.issues.forEach(issue => {
				const path = String(issue.path[0] ?? '');
				if (path) fieldErrors[path] = issue.message;
			});
			setErrors(fieldErrors);
			const uniquePaths = [...new Set(result.error.issues.map(i => String(i.path[0] ?? '')).filter(Boolean))];
			const labels = uniquePaths.map(p => PLAN_FIELD_LABELS[p] ?? p).join(', ');
			toast.error(`Campos inválidos: ${labels}`);
			return;
		}

		setErrors({});
		setIsLoading(true);
		try {
			const priceInReais = isFree ? 0 : Number.parseFloat(form.price.replace(',', '.')) || 0;

			const payload = {
				name: form.name,
				description: form.description,
				hierarchy: form.hierarchy,
				paymentMethods: form.paymentMethods,
				price: priceInReais,
				recurrentPayment: !isFree && form.recurrentPayment === 'yes',
				isListable: form.isListable === 'yes',
				interval: (form.interval || 'month') as PlanInterval,
				intervalCount: Number.parseInt(form.intervalCount, 10) || 1,
				gracePeriod: !isFree && form.gracePeriod ? Number.parseInt(form.gracePeriod, 10) : null,
				notifyUnpayment: !isFree && form.notifyUnpayment === 'yes',
				notifyInvoice: !isFree && form.notifyInvoice === 'yes',
				maxInstallments: hasCard ? Number.parseInt(form.maxInstallments, 10) || 1 : 1,
				maxProps: Number.parseInt(form.maxProps, 10) || 0,
				maxSamples: Number.parseInt(form.maxSamples, 10) || 0,
				advantages: form.advantages.filter(a => a.trim()),
			};

			if (id) {
				await updatePlan({ planId: id, ...payload });
			} else {
				await createPlan(payload);
			}
			toast.success('Plano salvo com sucesso!');
			onSubmit();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Erro ao salvar plano');
		} finally {
			setIsLoading(false);
		}
	};

	return {
		form,
		advantageKeys,
		errors,
		isLoading,
		isFree,
		hasCard,
		hierarchyOptions,
		set,
		setPaymentMethods,
		setAdvantage,
		addAdvantage,
		removeAdvantage,
		handleSubmit,
		isDirty,
	};
};
